import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  State,
  StateEvent,
  StateNode,
  Workflow,
  WorkflowMachine
} from '@sierralabs/workflow';

import { PartialPick } from '../../common/types/common.interface';
import { UserContext } from '../../user/User.context';
import { User } from '../../user/User.interface';
import { useWorkflowContext } from '../workflow/Workflow.context';
import { getStateNodeByStatus } from '../workflow/Workflow.hooks';
import { WorkflowInvokeEvent } from '../workflow/Workflow.interface';
import servicePlugins from '../workflow/plugins/services';
import transitionConditionPlugins from '../workflow/plugins/transition-conditions';
import { SubTaskUser, Task, TaskEntity } from './Task.interface';

export interface TaskContext {
  event: StateEvent | undefined;
  task: Task;
  taskEntity?: TaskEntity;
  user: User;
  workflow: Workflow;
  reason?: string | null;
  subTaskUserIds?: SubTaskUser[];
  /** List of transition condition plugins to skip. */
  skipTransitionConditionPlugins?: string[];
  /** List of service plugins to skip. */
  skipServicePlugins?: string[];
  /**
   * For Jira plugins that show a skip option whenever
   * a Task doesn't have any (filtered) Jira issues to update.
   */
  autoSkipJiraPlugins?: string[];
  /**
   * Additional plugin data added to the Task context
   * (e.g. additional options from a rendered component from a service).
   */
  pluginData?: TaskContextPluginData;
}

export interface TaskContextPluginData {
  /**
   * Data for the `CreateNewFileVersionConfig` Workflow transition condition.
   */
  createNewFileVersionConfig?: CreateNewFileVersionConfigTaskContextData;
}

export interface CreateJiraIssueParams {
  src: 'createJiraIssue';
  data: {
    projectId?: string;
    issueTypeId?: string;
    linkToEntity?: boolean;
    duplicateTitle?: boolean;
    duplicateDescription?: boolean;
    enableTitleTemplate?: boolean;
    titleTemplate?: string;
  };
}

export interface CreateSubTaskParams {
  src: 'createSubTasks';
  data: {
    /** @deprecated Use `groups` instead. */
    groupIds?: number[];
    groups: { id: number; name: string }[];
    workflow: { id: number; name: string };
    numberOfTasks: number;
    dialogTitle?: string;
    dialogMessage?: string;
  };
}

export interface RemoveSubTasksParams {
  src: 'removeSubTasks';
  data: {
    /**
     * Remove SubTasks with state paths from this list of state paths.
     * If there are no state paths configured, all SubTasks should be removed.
     */
    statePaths?: string[];
  };
}

export interface UpdateRelatedEntityStatusParams {
  src: 'updateRelatedEntityStatus';
  data: {
    entityName: string; // entitySchema.name
    relations: {
      name?: string; // user friendly display name
      // join relations; i.e. propertyName[0]->...propertyName[n]
      propertyNames: string[];
      taskType: string; // taskType.name
      events: {
        state: string; // stateNode.name
        event: string; // event.name
        target: string; // transition.target
      }[];
    }[];
  };
}

export interface UpdateJiraIssueParams {
  src: 'updateJiraIssue';
  data: {
    projectId: string;
    fields: {
      /** Entity column property name. */
      entityField: string;
      issueType?: { id: string; name: string };
      issueField: string;
    }[];
  };
}

export interface TransitionJiraIssueParams {
  src: 'transitionJiraIssue';
  data: {
    projectId: string | undefined;
    issues: {
      issueType?: { id: string; label: string };
      statuses?: { id: string; name: string }[];
    }[];
  };
}

export interface UpdateFieldFromJiraParams {
  src: 'updateFieldFromJira';
  data: {
    projectId?: string;
    fields?: {
      entityField?: string;
      issueType?: { id: string; label: string };
      issueField?: string;
    }[];
  };
}

export interface TransitionEntityStatusFromJiraParams {
  src: 'transitionEntityStatusFromJira';
  data: {
    projectId?: string;
    eventName?: string;
    partial?: boolean;
    issues?: {
      statuses?: { id: string; name: string }[];
      issueType?: { id: string; label: string };
    }[];
  };
}

export interface PublishEventParams {
  src: 'publishEvent';
  data: {
    events?: WorkflowInvokeEvent[];
  };
}

export interface RequireFieldsParams {
  requiredFields: string[];
}

export interface CreateNewFileVersionConfigParams {
  allowOptionalTrainingUpdates?: boolean;
}

export interface CreateNewFileVersionConfigTaskContextData {
  skipTrainingUpdates?: boolean;
}

export interface CheckSubTasksParams {
  path: string;
  partial?: boolean;
}

export interface SendEmailParams {
  src: 'sendEmail';
  data: {
    groupIds?: number[];
    userIds?: number[];
    specificEmailIds?: string;
    includeLinkToItem?: boolean;
    message?: string;
  };
}

export function useGetWorkflowMachineState(task?: Task) {
  const { user } = useContext(UserContext);
  const { getWorkflow } = useWorkflowContext();
  const [workflowMachineState, setWorkflowMachineState] = useState<{
    workflow?: Workflow;
    machine?: WorkflowMachine<TaskContext>;
    stateNode?: StateNode;
    render?: JSX.Element;
  }>({});
  const workflowMachineStateRef = useRef(workflowMachineState);
  const machineOptions = useMemo(() => {
    const render = (element?: JSX.Element) => {
      setWorkflowMachineState({
        ...workflowMachineStateRef.current,
        render: element
      });
    };
    return {
      guards: transitionConditionPlugins.reduce((guards, plugin) => {
        guards[plugin.type] = (state: State<TaskContext>, params: any) =>
          plugin.guard(state, params, render);
        return guards;
      }, {} as any),
      services: servicePlugins.reduce((services, plugin) => {
        services[plugin.src] = (state: State<TaskContext>, params: any) =>
          plugin.service(state, params, render);
        return services;
      }, {} as any)
    };
  }, []);

  const canTransition = useCallback(
    (event: StateEvent, task: Task, taskEntity: TaskEntity, user: User) => {
      const { stateNode, machine, workflow } = workflowMachineStateRef.current;
      const state: State<TaskContext> = {
        path: stateNode!.path,
        context: { event, task, taskEntity, user, workflow: workflow! }
      };
      return machine?.canTransition(state, event.name);
    },
    []
  );

  const handleTransition = useCallback(
    async (context: Omit<TaskContext, 'workflow'>) => {
      const { stateNode, machine, workflow } = workflowMachineStateRef.current;
      const state: State<TaskContext> = {
        path: stateNode!.path,
        context: { workflow: workflow!, ...context }
      };
      const newState = await machine?.transition(state, context.event?.name);
      return newState;
    },
    []
  );

  const runMachine = useCallback(
    async (
      // make some fields optional
      context: PartialPick<TaskContext, 'event' | 'workflow' | 'user'>
    ) => {
      const workflow: Workflow | undefined = getWorkflow(context.task);
      if (!workflow) {
        return {};
      }
      const machine = new WorkflowMachine<TaskContext>(
        workflow,
        machineOptions
      );
      workflowMachineStateRef.current.workflow = workflow;
      workflowMachineStateRef.current.machine = machine;

      let stateNode = getStateNodeByStatus(context.task.status, workflow);
      if (!stateNode) {
        try {
          const state = await machine.start({
            event: undefined,
            workflow,
            user: user!,
            ...context
          });
          stateNode = workflow.getState(state.path);
          workflowMachineStateRef.current.stateNode = stateNode;
        } catch (error) {
          console.error('[runMachine] error:', error);
        }
      } else {
        workflowMachineStateRef.current.stateNode = stateNode;
      }
      return { workflow, machine, stateNode };
    },
    [machineOptions, user, getWorkflow]
  );

  // run the machine when task changes
  useEffect(() => {
    (async () => {
      if (task) {
        const workflowMachineState = await runMachine({ task });
        setWorkflowMachineState({ ...workflowMachineState });
      }
    })();
  }, [task, runMachine]);
  return {
    ...workflowMachineState,
    runMachine,
    canTransition,
    handleTransition
  };
}
